import React, { useEffect, useState } from "react";
import { Link, Navigate, useParams, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  ArrowLeft,
  CheckCircle2,
  XCircle,
  FileText,
  ShieldCheck,
  Loader2,
  Camera,
  IdCard,
  Mail,
  Phone,
  MapPin,
  CalendarDays,
  Scan,
  RefreshCw,
  AlertTriangle,
} from "lucide-react";

export default function KYCDetail() {
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const [notes, setNotes] = useState("");
  const [rerunning, setRerunning] = useState(false);

  const rerunFaceMatch = async () => {
    setRerunning(true);
    try {
      const { data: r } = await api.post(`/admin/kyc/${id}/face-match/rerun`);
      setData((d) => d ? { ...d, face_match: r.face_match } : d);
      toast.success(`Face match: ${r.face_match.status}${r.face_match.similarity != null ? ` · ${r.face_match.similarity}%` : ""}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRerunning(false); }
  };

  useEffect(() => {
    if (!user || user === false || user.role !== "admin") return;
    api.get(`/admin/kyc/${id}`).then(({ data }) => {
      setData(data);
      setNotes(data.kyc_notes || "");
      setLoading(false);
    }).catch((e) => { toast.error(formatApiError(e)); setLoading(false); });
  }, [user, id]);

  if (user === null) {
    return <div className="min-h-screen flex items-center justify-center text-[#4F4D45]">Verifying access…</div>;
  }
  if (user === false) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  if (user.role !== "admin") return <Navigate to="/app" replace />;

  const act = async (decision) => {
    setActing(decision);
    try {
      await api.post(`/admin/kyc/${id}/review`, { decision, notes });
      toast.success(`Account ${decision}.`);
      navigate("/admin");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setActing(null); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-[#4F4D45]">Loading…</div>;
  }
  if (!data) return <div className="p-10 text-center text-[#4F4D45]">Not found.</div>;

  const docs = data.documents || {};
  const liveness = docs.liveness_frames || [];

  return (
    <div className="min-h-screen bg-[#F0ECE0]">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#F0ECE0]/80 border-b border-[#D2CDBE]">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-2 text-sm text-[#4F4D45] hover:text-[#1B3526]" data-testid="kyc-back">
            <ArrowLeft className="w-4 h-4" /> Back to queue
          </Link>
          <div className="text-eyebrow text-[#4F4D45]">KYC submission</div>
        </div>
      </header>

      <main className="max-w-[1280px] mx-auto px-6 md:px-10 py-8 grid lg:grid-cols-3 gap-6">
        {/* Identity */}
        <section className="lg:col-span-1 fv-card p-6">
          <div className="text-eyebrow text-[#4F4D45]">Applicant</div>
          <h2 className="font-display text-2xl font-bold tracking-tight mt-1" data-testid="kyc-applicant-name">{data.name}</h2>
          <div className="mt-1 text-sm text-[#4F4D45]">{data.account_type === "business" ? data.business_name || "Business" : "Personal"}</div>

          <ul className="mt-6 space-y-3 text-sm">
            <li className="flex items-start gap-3"><Mail className="w-4 h-4 text-[#1B3526] mt-0.5" /><div><div className="text-[#4F4D45] text-xs">Email</div><div>{data.email}</div></div></li>
            <li className="flex items-start gap-3"><Phone className="w-4 h-4 text-[#1B3526] mt-0.5" /><div><div className="text-[#4F4D45] text-xs">Phone</div><div>{data.phone || "—"}</div></div></li>
            <li className="flex items-start gap-3"><CalendarDays className="w-4 h-4 text-[#1B3526] mt-0.5" /><div><div className="text-[#4F4D45] text-xs">Date of birth</div><div>{data.date_of_birth || "—"}</div></div></li>
            <li className="flex items-start gap-3"><MapPin className="w-4 h-4 text-[#1B3526] mt-0.5" /><div><div className="text-[#4F4D45] text-xs">Residential address</div><div className="leading-relaxed">{[data.address?.line1, data.address?.line2, data.address?.city, data.address?.state, data.address?.postal_code, data.address?.country].filter(Boolean).join(", ") || "—"}</div></div></li>
            <li className="flex items-start gap-3"><ShieldCheck className="w-4 h-4 text-[#1B3526] mt-0.5" /><div><div className="text-[#4F4D45] text-xs">Wallet ID</div><div className="font-mono">{data.wallet_id}</div></div></li>
            <li className="flex items-start gap-3"><CheckCircle2 className={`w-4 h-4 mt-0.5 ${data.liveness_passed ? "text-[#264C39]" : "text-[#4F4D45]"}`} /><div><div className="text-[#4F4D45] text-xs">Liveness</div><div data-testid="kyc-liveness-status">{data.liveness_passed ? "Passed" : "Not performed"} · {liveness.length} extra frames</div></div></li>
            <li className="flex items-start gap-3">
              <Scan className={`w-4 h-4 mt-0.5 ${data.face_match?.passed ? "text-[#264C39]" : data.face_match?.status === "below_threshold" || data.face_match?.status === "no_match" ? "text-[#992F2F]" : "text-[#4F4D45]"}`} />
              <div><div className="text-[#4F4D45] text-xs">Face match (selfie ↔ ID)</div>
                <div data-testid="kyc-face-match-status">
                  {data.face_match?.similarity != null ? `${data.face_match.similarity}% · ` : ""}
                  {(data.face_match?.status || "not_run").replace(/_/g, " ")}
                </div>
              </div>
            </li>
          </ul>

          <div className={`mt-6 inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full ${
            data.kyc_status === "approved" ? "bg-[#264C39]/10 text-[#264C39]" :
            data.kyc_status === "rejected" ? "bg-[#992F2F]/10 text-[#992F2F]" :
            "bg-[#B58957]/20 text-[#8A4632]"
          }`} data-testid="kyc-status-badge">
            Status: {data.kyc_status}
            {data.kyc_reviewed_by === "auto" && <span className="ml-1.5 text-[10px] uppercase tracking-wider opacity-80">· auto</span>}
          </div>
        </section>

        {/* Documents */}
        <section className="lg:col-span-2 space-y-5">
          <DocCard
            title="Selfie / Photograph"
            meta={data.documents_meta?.photo}
            doc={docs.photo}
            Icon={Camera}
            testId="kyc-doc-photo"
          />
          <DocCard
            title="Government-issued ID"
            meta={data.documents_meta?.id_document}
            doc={docs.id_document}
            Icon={IdCard}
            testId="kyc-doc-id"
          />
          <DocCard
            title="Proof of address"
            meta={data.documents_meta?.proof_of_address}
            doc={docs.proof_of_address}
            Icon={FileText}
            testId="kyc-doc-proof"
          />

          {liveness.length > 0 && (
            <div className="fv-card p-5">
              <div className="text-eyebrow text-[#4F4D45]">Liveness frames</div>
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="kyc-liveness-frames">
                {liveness.map((f, i) => (
                  <img
                    key={i}
                    src={`data:${f.content_type};base64,${f.data}`}
                    alt={`frame-${i}`}
                    className="w-full aspect-square object-cover rounded-lg border border-[#D2CDBE]"
                  />
                ))}
              </div>
            </div>
          )}

          {/* Face match (AWS Rekognition CompareFaces) */}
          <FaceMatchPanel match={data.face_match} onRerun={rerunFaceMatch} rerunning={rerunning} />

          {/* Review action */}
          <div className="fv-card p-5">
            <div className="text-eyebrow text-[#4F4D45]">Reviewer decision</div>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Internal notes (optional) — visible only to admins."
              className="mt-3"
              data-testid="kyc-review-notes"
            />
            <div className="mt-4 flex flex-wrap gap-3">
              <Button
                onClick={() => act("approved")}
                disabled={acting !== null}
                className="bg-[#1B3526] hover:bg-[#0F2517]"
                data-testid="kyc-approve-button"
              >
                {acting === "approved" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-2" /> Approve</>}
              </Button>
              <Button
                onClick={() => act("rejected")}
                disabled={acting !== null}
                variant="outline"
                className="border-[#992F2F] text-[#992F2F] hover:bg-[#992F2F] hover:text-white"
                data-testid="kyc-reject-button"
              >
                {acting === "rejected" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><XCircle className="w-4 h-4 mr-2" /> Reject</>}
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

function DocCard({ title, meta, doc, Icon, testId }) {
  const isImage = (meta?.content_type || "").startsWith("image/");
  const dataUrl = doc?.data ? `data:${doc.content_type};base64,${doc.data}` : null;
  return (
    <div className="fv-card p-5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#1B3526]/10 text-[#1B3526] flex items-center justify-center">
            <Icon className="w-5 h-5" strokeWidth={1.5} />
          </div>
          <div>
            <div className="font-medium">{title}</div>
            <div className="text-xs text-[#4F4D45]">{meta?.name || "—"} · {meta?.size ? `${(meta.size/1024).toFixed(0)} KB` : ""} · {meta?.content_type}</div>
          </div>
        </div>
        {dataUrl && (
          <a href={dataUrl} download={meta?.name || "document"} className="text-xs text-[#1B3526] hover:underline" data-testid={`${testId}-download`}>Download</a>
        )}
      </div>
      <div className="mt-4 rounded-xl overflow-hidden border border-[#D2CDBE] bg-[#F0ECE0]">
        {isImage && dataUrl ? (
          <img src={dataUrl} alt={title} className="w-full max-h-[460px] object-contain bg-white" />
        ) : dataUrl ? (
          <div className="p-6 text-center text-sm text-[#4F4D45]">
            PDF document — <a href={dataUrl} target="_blank" rel="noreferrer" className="text-[#1B3526] underline">open in new tab</a>
          </div>
        ) : (
          <div className="p-6 text-center text-sm text-[#4F4D45]">No file uploaded.</div>
        )}
      </div>
    </div>
  );
}

function FaceMatchPanel({ match, onRerun, rerunning }) {
  const status = match?.status || "not_run";
  const similarity = match?.similarity;
  const threshold = match?.threshold ?? 85;
  const STATUS_UI = {
    matched: { color: "bg-[#264C39]/10 text-[#264C39] border-[#264C39]/30", label: "Match passed", Icon: CheckCircle2 },
    below_threshold: { color: "bg-[#992F2F]/10 text-[#992F2F] border-[#992F2F]/30", label: "Below threshold", Icon: AlertTriangle },
    no_match: { color: "bg-[#992F2F]/10 text-[#992F2F] border-[#992F2F]/30", label: "No match", Icon: XCircle },
    no_face: { color: "bg-[#B58957]/20 text-[#8A4632] border-[#B58957]", label: "No face detected", Icon: AlertTriangle },
    error: { color: "bg-[#992F2F]/10 text-[#992F2F] border-[#992F2F]/30", label: "Error", Icon: AlertTriangle },
    decode_error: { color: "bg-[#992F2F]/10 text-[#992F2F] border-[#992F2F]/30", label: "Image decode error", Icon: AlertTriangle },
    missing_image: { color: "bg-[#B58957]/20 text-[#8A4632] border-[#B58957]", label: "Missing image", Icon: AlertTriangle },
    not_configured: { color: "bg-[#E3DECF] text-[#4F4D45] border-[#D2CDBE]", label: "AWS keys not configured", Icon: Scan },
    not_run: { color: "bg-[#E3DECF] text-[#4F4D45] border-[#D2CDBE]", label: "Not run", Icon: Scan },
  };
  const meta = STATUS_UI[status] || STATUS_UI.not_run;
  const Icon = meta.Icon;
  // similarity bar: 0% red → 100% green. clamp similarity for the bar.
  const pct = similarity != null ? Math.max(0, Math.min(100, similarity)) : null;
  return (
    <div className="fv-card p-5" data-testid="kyc-face-match-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#1B3526]/10 text-[#1B3526] flex items-center justify-center">
            <Scan className="w-5 h-5" strokeWidth={1.5} />
          </div>
          <div>
            <div className="font-medium">Face match · AWS Rekognition</div>
            <div className="text-xs text-[#4F4D45]">Selfie compared to the photo on the government-issued ID. Threshold {threshold}%.</div>
          </div>
        </div>
        <Button
          onClick={onRerun}
          disabled={rerunning}
          variant="outline"
          className="border-[#D2CDBE]"
          data-testid="kyc-face-match-rerun"
        >
          {rerunning ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Re-run
        </Button>
      </div>

      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${meta.color}`} data-testid="kyc-face-match-badge">
          <Icon className="w-3.5 h-3.5" />
          {meta.label}
        </span>
        {similarity != null && (
          <span className="text-sm font-mono" data-testid="kyc-face-match-similarity">{similarity}%</span>
        )}
      </div>

      {pct != null && (
        <div className="mt-3">
          <div className="h-2 rounded-full bg-[#E3DECF] overflow-hidden">
            <div
              className={`h-full ${pct >= threshold ? "bg-[#264C39]" : "bg-[#A85943]"} transition-all`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="mt-1 text-[10px] text-[#4F4D45] flex justify-between">
            <span>0%</span><span>Threshold {threshold}%</span><span>100%</span>
          </div>
        </div>
      )}

      {match?.error && (
        <div className="mt-3 text-xs text-[#992F2F] bg-[#992F2F]/5 border border-[#992F2F]/30 rounded-md p-2 break-words">
          {match.error}
        </div>
      )}
      {status === "not_configured" && (
        <div className="mt-3 text-xs text-[#4F4D45] leading-relaxed">
          AWS Rekognition isn't configured yet. Add <span className="font-mono">AWS_ACCESS_KEY_ID</span>, <span className="font-mono">AWS_SECRET_ACCESS_KEY</span>, and <span className="font-mono">AWS_REGION</span> to the backend env, restart, then click <strong>Re-run</strong>.
        </div>
      )}
    </div>
  );
}

